import { Theme, useTheme } from '@mui/material/styles';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  CARD_CVC,
  CARD_DATE,
  CARD_NUMBER,
  CARD_POSTAL
} from 'payments/components/CardFieldsContainer';
import { DEFAULT_CARD_FIELD_STATES } from 'payments/constants/card';
import { useCloverPayments } from 'payments/hooks/usePayments';
import { type CardFieldStates } from 'payments/types/card';
import {
  type Clover,
  CloverElements,
  ImageStyles,
  InputPlaceholderStyles,
  InputStyles
} from 'payments/types/clover';
import { CIRCULAR_STD } from 'reservations/brand/constants';
import { isServer } from 'utility/isServer';

let clover: null | Clover = null;

let theme: undefined | Theme = undefined;

const formatCloverError = (
  errorKey: keyof CloverElements,
  errorMessage: string
) => {
  if (!errorMessage) {
    return;
  }

  switch (errorKey) {
    case 'CARD_DATE':
      return errorMessage.replace('expiry', 'expiration date');
    case 'CARD_POSTAL_CODE':
      return errorMessage.replace('Card postal', 'Zip');
    default:
      return errorMessage;
  }
};

const getCardElementStyles = () => {
  const cardElements = [CARD_NUMBER, CARD_DATE, CARD_CVC, CARD_POSTAL];
  const styles: {
    [key: string]: InputStyles | InputPlaceholderStyles | ImageStyles;
  } = {};

  // These map to the selectors defined in the Clover iFrame styling docs
  // https://docs.clover.com/docs/customizing-iframe-elements-with-css
  cardElements.forEach(elementStyleSelector => {
    styles[`${elementStyleSelector} input`] = {
      fontSize: '16px',
      height: '23px',
      width: '100%',
      color: theme ? theme.typography.body1.color : '#47558D',
      fontFamily: `${CIRCULAR_STD}, "Helvetica Neue", Arial, sans-serif`,
      backgroundColor: 'transparent'
    };
    styles[`${elementStyleSelector} input::placeholder`] = {
      color: 'transparent'
    };
    styles[`${elementStyleSelector} img`] = { display: 'none' };
  });

  return styles;
};

const createCardElements = (cloverElements: CloverElements) => {
  const styles = getCardElementStyles();

  const cardNumber = cloverElements.create('CARD_NUMBER', styles);
  const cardDate = cloverElements.create('CARD_DATE', styles);
  const cardCvv = cloverElements.create('CARD_CVV', styles);
  const cardPostalCode = cloverElements.create('CARD_POSTAL_CODE', styles);

  cardNumber.mount(`#${CARD_NUMBER}`);
  cardDate.mount(`#${CARD_DATE}`);
  cardCvv.mount(`#${CARD_CVC}`);
  cardPostalCode.mount(`#${CARD_POSTAL}`);

  return [cardNumber, cardDate, cardCvv, cardPostalCode];
};

const initClover = (
  cloverPlatformPublicKey: string,
  setCardFieldStates: Dispatch<SetStateAction<CardFieldStates>>
) => {
  // NOTE: Unable to resolve this TS error, as the new keywords makes it expect
  // a class constructor which we don't have.
  //@ts-expect-error
  clover = new window.Clover(cloverPlatformPublicKey);
  const cloverElements = clover?.elements();

  if (cloverElements) {
    const cardElements = createCardElements(cloverElements);
    cardElements.forEach(element => {
      const { elementType } = element;

      element.addEventListener('blur', event => {
        setCardFieldStates(currentFieldStates => ({
          ...currentFieldStates,
          [elementType]: {
            ...currentFieldStates[elementType as keyof CardFieldStates],
            error: formatCloverError(
              elementType,
              event[elementType as keyof CardFieldStates].error ?? ''
            )
          }
        }));
      });

      element.addEventListener('focus', () => {
        setCardFieldStates(currentFieldStates => ({
          ...currentFieldStates,
          [elementType]: {
            ...currentFieldStates[elementType as keyof CardFieldStates],
            isFocused: true
          }
        }));
      });
    });
  }
};

export const useClover = () => {
  const { setValue } = useFormContext();
  const { cloverPaymentFormUrl, cloverPlatformPublicKey } = useCloverPayments();
  const [cardFieldStates, setCardFieldStates] = useState<CardFieldStates>(
    DEFAULT_CARD_FIELD_STATES
  );
  const [loadedClover, setLoadedClover] = useState(!!window.Clover);

  theme = useTheme();

  const onLoad = () => {
    setLoadedClover(true);
    initClover(cloverPlatformPublicKey, setCardFieldStates);
  };

  useEffect(() => {
    if (!isServer() && !loadedClover) {
      const script = document.createElement('script');
      script.src = cloverPaymentFormUrl;
      document.body.appendChild(script);
      script.onload = () => onLoad();
    }
  }, []);

  // The clover elements can become unmounted if the page rerenders, this mounts them again
  useEffect(() => {
    const iframes = Array.from(document.querySelectorAll('iframe'));
    if (loadedClover && !iframes.some(el => el.title === 'CARD NUMBER')) {
      initClover(cloverPlatformPublicKey, setCardFieldStates);
    }
  });

  const handleCardSubmit = async () => {
    const cloverResult = await clover?.createToken();

    if (!cloverResult) {
      return {};
    }

    const { card, errors, token } = cloverResult;

    if (errors) {
      Object.entries(errors).forEach(([errorKey, errorMessage]) => {
        setCardFieldStates(currentFieldStates => ({
          ...currentFieldStates,
          [errorKey]: {
            ...currentFieldStates[errorKey as keyof CardFieldStates],
            error: formatCloverError(
              errorKey as keyof CloverElements,
              errorMessage
            )
          }
        }));
      });
      return { errors };
    }

    if (!token) {
      console.warn(
        'Clover Checkout - token not present in createToken response',
        cloverResult
      );
      return {};
    }

    setValue('token', token);
    setValue('cardDetails', card);

    return { card, token };
  };

  return {
    cardFieldStates,
    handleCardSubmit,
    setCardFieldStates
  };
};
